import { useState, useEffect, useRef } from 'react';
import { ScanBarcode, Loader2, Camera, X, AlertCircle, ExternalLink } from 'lucide-react';
import type { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { triggerHaptic } from '@/hooks/useHaptics';
import { supabase } from '@/integrations/supabase/client';
import { useSavedItems } from '@/hooks/useSavedItems';
import { useAuth } from '@/hooks/useAuth';
import { AuthDialog } from '@/components/auth/AuthDialog';
import { useRegionalSettings } from '@/hooks/useRegionalSettings';

interface BarcodeScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBarcodeDetected?: (barcode: string, product: ScannedProduct | null) => void;
}

interface RetailerPrice {
  store: string;
  price: number;
  currency: string;
  url: string;
  inStock: boolean;
  isBestPrice?: boolean;
}

interface ScannedProduct {
  name: string | null;
  brand: string | null;
  image: string | null;
  category: string | null;
  barcode: string;
  source: 'openfoodfacts' | 'upcitemdb' | 'unknown';
  found: boolean;
  prices?: RetailerPrice[];
  bestPrice?: RetailerPrice;
}

type ScannerState = 'idle' | 'starting' | 'scanning' | 'detected' | 'error';

// Barcode formats function - dynamically loaded to avoid bundling html5-qrcode in main bundle
const getBarcodeFormats = async () => {
  const { Html5QrcodeSupportedFormats } = await import('html5-qrcode');
  return [
    Html5QrcodeSupportedFormats.UPC_A,
    Html5QrcodeSupportedFormats.UPC_E,
    Html5QrcodeSupportedFormats.EAN_13,
    Html5QrcodeSupportedFormats.EAN_8,
    Html5QrcodeSupportedFormats.CODE_128,
    Html5QrcodeSupportedFormats.CODE_39,
    Html5QrcodeSupportedFormats.CODE_93,
    Html5QrcodeSupportedFormats.ITF,
  ];
};

// Real product lookup using barcode-lookup edge function
const lookupBarcode = async (barcode: string, countryCode: string): Promise<ScannedProduct | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('barcode-lookup', {
      body: { barcode, countryCode },
    });

    if (error) {
      console.error('Barcode lookup error:', error);
      return null;
    }

    if (data?.success && data?.data) {
      return data.data as ScannedProduct;
    }

    return null;
  } catch (error) {
    console.error('Barcode lookup error:', error);
    return null;
  }
};

export function BarcodeScannerDialog({ open, onOpenChange, onBarcodeDetected }: BarcodeScannerDialogProps) {
  const [manualBarcode, setManualBarcode] = useState('');
  const [scannerState, setScannerState] = useState<ScannerState>('idle');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState<string | null>(null);
  const [scannedProduct, setScannedProduct] = useState<ScannedProduct | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  
  const { user } = useAuth();
  const { createItem } = useSavedItems();
  const { settings, formatPrice } = useRegionalSettings();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerId = 'barcode-scanner-container';

  // Auto-start scanner when dialog opens, cleanup when closes
  useEffect(() => {
    if (open) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        startScanner();
      }, 200);
      return () => clearTimeout(timer);
    } else {
      stopScanner();
      resetState();
    }
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const resetState = () => {
    setManualBarcode('');
    setScannerState('idle');
    setScannedBarcode(null);
    setScannedProduct(null);
    setErrorMessage(null);
    setIsLookingUp(false);
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) { // Html5QrcodeScannerState.SCANNING
          await scannerRef.current.stop();
        }
      } catch (error) {
        // Scanner might already be stopped
        console.debug('Scanner stop error (expected if already stopped):', error);
      }
      scannerRef.current = null;
    }
  };

  const startScanner = async () => {
    setScannerState('starting');
    setErrorMessage(null);
    triggerHaptic('light');

    try {
      // Wait for the container to be in the DOM
      await new Promise(resolve => setTimeout(resolve, 100));

      const container = document.getElementById(scannerContainerId);
      if (!container) {
        throw new Error('Scanner container not found');
      }

      // Dynamically import Html5Qrcode to keep it out of main bundle (saves 328KB)
      const { Html5Qrcode } = await import('html5-qrcode');
      const barcodeFormats = await getBarcodeFormats();

      // Initialize scanner
      scannerRef.current = new Html5Qrcode(scannerContainerId, {
        formatsToSupport: barcodeFormats,
        verbose: false,
      });

      // Start scanning with back camera
      await scannerRef.current.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 280, height: 120 }, // Horizontal rectangle for barcodes
          aspectRatio: 1.0,
        },
        onScanSuccess,
        onScanFailure
      );

      setScannerState('scanning');
    } catch (error: any) {
      console.error('Failed to start scanner:', error);
      setScannerState('error');
      
      if (error.message?.includes('Permission denied') || error.name === 'NotAllowedError') {
        setErrorMessage('Camera access denied. Please allow camera access in your browser settings, or enter the barcode manually below.');
      } else if (error.message?.includes('No camera') || error.name === 'NotFoundError') {
        setErrorMessage('No camera found. Please enter the barcode manually below.');
      } else {
        setErrorMessage('Could not start camera. Please enter the barcode manually below.');
      }
    }
  };

  const onScanSuccess = async (decodedText: string) => {
    // Stop scanning immediately after detection
    await stopScanner();
    
    // Haptic feedback on successful scan
    triggerHaptic('success');
    
    setScannedBarcode(decodedText);
    setScannerState('detected');
    
    // Look up the product
    await handleLookup(decodedText);
  };

  const onScanFailure = (errorMessage: string) => {
    // This is called frequently when no barcode is in frame - ignore it
    // Only log for debugging if needed
    // console.debug('Scan frame failed:', errorMessage);
  };

  const handleLookup = async (barcode: string) => {
    if (!barcode.trim()) return;
    
    setIsLookingUp(true);
    setScannedBarcode(barcode);
    
    try {
      const product = await lookupBarcode(barcode, settings.countryCode);
      setScannedProduct(product);
      
      if (!product || !product.found) {
        toast.info('Product not found in database', {
          description: `Barcode: ${barcode}`,
        });
      }
    } catch (error) {
      toast.error('Failed to look up product');
      setScannedProduct(null);
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleManualLookup = () => {
    if (!manualBarcode.trim()) return;
    triggerHaptic('light');
    setScannerState('detected');
    handleLookup(manualBarcode.trim());
  };

  const handleSave = async () => {
    if (!user) {
      setShowAuthDialog(true);
      return;
    }

    if (!scannedProduct) return;

    setIsSaving(true);
    triggerHaptic('light');

    try {
      // Build the saved item data from scanned product
      const bestPrice = scannedProduct.bestPrice;
      
      await createItem.mutateAsync({
        source_type: 'barcode',
        name: scannedProduct.name || `Product ${scannedProduct.barcode}`,
        image_url: scannedProduct.image || undefined,
        price: bestPrice?.price || undefined,
        currency: bestPrice?.currency || 'USD',
        store: bestPrice?.store || undefined,
        url: bestPrice?.url || undefined,
        note: `Barcode: ${scannedProduct.barcode}`,
        ai_category: scannedProduct.category || undefined,
        ai_tags: scannedProduct.brand ? [scannedProduct.brand] : undefined,
        save_source: 'barcode',
      });

      triggerHaptic('success');
      toast.success('Item added to Saves!');
      
      if (onBarcodeDetected && scannedBarcode) {
        onBarcodeDetected(scannedBarcode, scannedProduct);
      }
      
      handleClose();
    } catch (error) {
      console.error('Failed to save item:', error);
      triggerHaptic('error');
      toast.error('Failed to save item');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTryAgain = async () => {
    setScannedBarcode(null);
    setScannedProduct(null);
    setScannerState('idle');
    setErrorMessage(null);
  };

  const handleClose = () => {
    stopScanner();
    resetState();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanBarcode className="w-5 h-5" />
            Scan Barcode
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Idle state - show start button */}
          {scannerState === 'idle' && !scannedProduct && (
            <>
              <button
                onClick={startScanner}
                className="w-full aspect-video rounded-xl border-2 border-dashed border-muted-foreground/25 hover:border-primary/50 hover:bg-accent transition-colors flex flex-col items-center justify-center gap-3"
              >
                <Camera className="w-12 h-12 text-muted-foreground" />
                <span className="text-sm font-medium">Tap to start scanning</span>
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    or enter manually
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder="Enter barcode number..."
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualLookup()}
                  className="flex-1"
                />
                <Button 
                  onClick={handleManualLookup} 
                  disabled={!manualBarcode.trim()}
                >
                  Look up
                </Button>
              </div>
            </>
          )}

          {/* Starting and Scanning states - container must be in DOM for html5-qrcode */}
          {(scannerState === 'starting' || scannerState === 'scanning') && (
            <div className="space-y-3">
              <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black">
                {/* Scanner video container - always present for html5-qrcode to mount */}
                <div 
                  id={scannerContainerId} 
                  className="w-full h-full [&>video]:object-cover [&>video]:w-full [&>video]:h-full"
                />
                
                {/* Loading overlay while starting */}
                {scannerState === 'starting' && (
                  <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-3 z-10">
                    <Loader2 className="w-10 h-10 text-primary animate-spin" />
                    <span className="text-sm text-white">Starting camera...</span>
                  </div>
                )}
                
                {/* Scanning overlay - only when actively scanning */}
                {scannerState === 'scanning' && (
                  <div className="absolute inset-0 pointer-events-none">
                    {/* Scanning frame */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[120px]">
                      {/* Corner brackets */}
                      <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary rounded-tl-lg" />
                      <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-primary rounded-tr-lg" />
                      <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-primary rounded-bl-lg" />
                      <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-primary rounded-br-lg" />
                      
                      {/* Scanning line */}
                      <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary animate-pulse" />
                    </div>
                  </div>
                )}
              </div>
              
              {scannerState === 'scanning' && (
                <>
                  <p className="text-center text-sm text-muted-foreground">
                    Point camera at a barcode
                  </p>
                  
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      stopScanner();
                      setScannerState('idle');
                    }}
                    className="w-full"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Stop Scanning
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Error state */}
          {scannerState === 'error' && (
            <>
              <div className="w-full aspect-video rounded-xl border-2 border-destructive/30 bg-destructive/5 flex flex-col items-center justify-center gap-3 p-4">
                <AlertCircle className="w-10 h-10 text-destructive" />
                <p className="text-sm text-center text-muted-foreground">
                  {errorMessage}
                </p>
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder="Enter barcode number..."
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualLookup()}
                  className="flex-1"
                />
                <Button 
                  onClick={handleManualLookup} 
                  disabled={!manualBarcode.trim()}
                >
                  Look up
                </Button>
              </div>
            </>
          )}

          {/* Looking up product */}
          {isLookingUp && (
            <div className="rounded-xl border bg-card p-6 flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <div className="text-center">
                <p className="font-medium">Looking up product...</p>
                <p className="text-sm text-muted-foreground">{scannedBarcode}</p>
              </div>
            </div>
          )}

          {/* Product found */}
          {scannedProduct && scannedProduct.found && !isLookingUp && (
            <div className="rounded-xl border bg-card p-4 space-y-3">
              <div className="flex gap-3">
                {scannedProduct.image ? (
                  <img
                    src={scannedProduct.image}
                    alt={scannedProduct.name || 'Product'}
                    className="w-20 h-20 rounded-lg object-cover bg-muted"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center">
                    <ScanBarcode className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {scannedProduct.brand && (
                    <p className="text-xs text-muted-foreground">{scannedProduct.brand}</p>
                  )}
                  <h4 className="font-medium text-foreground line-clamp-2">
                    {scannedProduct.name || 'Unknown Product'}
                  </h4>
                  {scannedProduct.bestPrice && (
                    <p className="text-primary font-semibold mt-1">
                      {formatPrice(scannedProduct.bestPrice.price, scannedProduct.bestPrice.currency)}
                      <span className="text-xs font-normal text-muted-foreground ml-1">
                        at {scannedProduct.bestPrice.store}
                      </span>
                    </p>
                  )}
                  {scannedProduct.category && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {scannedProduct.category}
                    </p>
                  )}
                </div>
              </div>

              {/* Price comparison from retailers */}
              {scannedProduct.prices && scannedProduct.prices.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Compare prices
                  </p>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {scannedProduct.prices.slice(0, 4).map((price, idx) => (
                      <a
                        key={idx}
                        href={price.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{price.store}</span>
                          {price.isBestPrice && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                              Best
                            </span>
                          )}
                          {!price.inStock && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive font-medium">
                              Out of stock
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="font-semibold">
                            {formatPrice(price.price, price.currency)}
                          </span>
                          <ExternalLink className="w-3 h-3 text-muted-foreground" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {!scannedProduct.prices?.length && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  No pricing data available
                </p>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={handleTryAgain} className="flex-1" disabled={isSaving}>
                  Scan Another
                </Button>
                <Button onClick={handleSave} className="flex-1" disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Add to Saves'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Product not found */}
          {scannedProduct && !scannedProduct.found && !isLookingUp && scannerState === 'detected' && (
            <div className="rounded-xl border bg-card p-4 space-y-3">
              <div className="text-center py-4">
                <AlertCircle className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                <p className="font-medium">Product not found</p>
                <p className="text-sm text-muted-foreground">Barcode: {scannedBarcode}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Try searching online for this barcode
                </p>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={handleTryAgain} className="flex-1">
                  Try Again
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.open(`https://www.google.com/search?q=${scannedBarcode}`, '_blank')}
                  className="flex-1"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Search
                </Button>
              </div>
            </div>
          )}

          {/* Lookup failed completely */}
          {!scannedProduct && !isLookingUp && scannerState === 'detected' && (
            <div className="rounded-xl border bg-card p-4 space-y-3">
              <div className="text-center py-4">
                <AlertCircle className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                <p className="font-medium">Lookup failed</p>
                <p className="text-sm text-muted-foreground">Barcode: {scannedBarcode}</p>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={handleTryAgain} className="flex-1">
                  Try Again
                </Button>
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center">
            Scan UPC, EAN, or other product barcodes
          </p>
        </div>
      </DialogContent>

      <AuthDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} />
    </Dialog>
  );
}
